import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DmcPackageService } from 'src/app/business-service/dmc-package/dmc-package.service';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { ComponentReloadService } from 'src/app/modules/shared/services/reload/component-reload.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-dmc-package-details',
  templateUrl: './dmc-package-details.component.html',
  styleUrls: ['./dmc-package-details.component.scss'],
})
export class DmcPackageDetailsComponent implements OnInit {
  @Input() packageId: any;
  @Input() storedFormData: any;

  @Input() editPackageId: any;

  totalNoOfNights: number = 0;

  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter<any>();
  @Output() assignPackageId: EventEmitter<any> = new EventEmitter<any>();
  @Output() destinationDataChanged: EventEmitter<any> = new EventEmitter<any>();

  packageDetailsForm!: FormGroup;

  // TODO: Use LOV from API
  dmcPackages: any = [
    {name: 'Adventure', key: 'Adventure'},
    {name: 'Heritage', key: 'Heritage'},
    {name: 'Family/Friends', key: 'Family/Friends'},
    {name: 'Honeymoon', key: 'Honeymoon'},
  ]

  constructor(
    private _globalService: GlobalService,
    private _fb: FormBuilder,
    private _validationService: InputValidationsService,
    private _dmcPackageService: DmcPackageService,
    private _responseService: ResponseNotificationService,
    private _reloadService: ComponentReloadService
  ) { }

  ngOnInit() {
    // Value initializations
    this.totalNoOfNights = 0;
    
    this.packageDetailsForm = this._fb.group({
      packageTitle: ['', Validators.required],
      packageReferenceId: [''],
      destinations: this._fb.array([this.createDestinationItem()]),
      packageStartDate: [null, Validators.required],
      packageEndDate: [null, Validators.required],
      packageType: ['Family/Friends'],
      notes: [''],
      packageId: [this.packageId],
      id: [null],
      organizationId: [null],
      ownership: [''],
    });

    this.packageDetailsForm.valueChanges.subscribe(value => {
      this.packageDetailsForm.markAsTouched();
      this.packageDetailsForm.markAsDirty();
    })

    this.packageDetailsForm.get('destinations')?.valueChanges.subscribe(value => {
      let totalDays: number = 0;
      for (let item of value) {
        totalDays += +item.days;
      }
      this.totalNoOfNights = totalDays;
    });

    if(this.storedFormData){
      this.patchFormData(this.storedFormData);
    }
  }

  ngOnChanges(){
    this.ngOnInit();
  }

  today(): Date{
    return new Date();
  }

  convertToDate(value: any): Date{
    return new Date(value);
  }

  createDestinationItem(): FormGroup {
    return this._fb.group({
      location: ['', Validators.required],
      days: [0, this._validationService.valueNotZeroValidator()]
    });
  }

  get destinationDaysArray(): FormArray {
    return this.packageDetailsForm.get('destinations') as FormArray;
  }

  addDestination() {
    this.destinationDaysArray.push(this.createDestinationItem());
  }

  removeDestination(index: number) {
    this.destinationDaysArray.removeAt(index);
    this.packageDetailsForm.markAsDirty();
    this.packageDetailsForm.markAsTouched();
  }

  get getTotalNightsAndDays(){
    let totalNoOfNights = Number(this.totalNoOfNights);
    return `${totalNoOfNights} Nights / ${totalNoOfNights+1} Days`
  }

  patchFormData(data: any) {
    this.packageDetailsForm.patchValue({
      ...data,
      packageStartDate: this.convertToDate(data.packageStartDate),
      packageEndDate: this.convertToDate(data.packageEndDate),
      destinations: this.patchDestinationsDays(data.destinations),
    });
    this.packageDetailsForm.markAsPristine();
  }

  patchDestinationsDays(storedDestinationsData: any) {
    const destinationsArray = this.destinationDaysArray;    
    storedDestinationsData.forEach((destinationDays: any, index: number) => {
      if (destinationsArray.length <= index) {
        destinationsArray.push(this.createDestinationItem());
      }
      destinationsArray.at(index).patchValue(destinationDays);
    });
  }

  async onSubmit(): Promise<boolean> {
    if (this.packageDetailsForm.valid) {
      let formData = this.serializeFormData(this.packageDetailsForm.value);
      
      return new Promise((resolve) => {
        this._dmcPackageService.addPackageDetails(formData).subscribe({
          next: (data: any) => {
            // Checking if destinations data is changed
            if(this.storedFormData){
              if(JSON.stringify(this.storedFormData.destinations) !== JSON.stringify(data.destinations)){
                this.destinationDataChanged.emit();
              }
            }

            this.storeFormData.emit(data);
            this.assignPackageId.emit(data.packageId);            
          },
          error: (error) => {
            console.error('Error saving package: ', error);
            this._responseService.showMessage("Error saving package", true);
            resolve(false);
          },
          complete: () => {
            this._reloadService.triggerDMCPackagesReload();
            this.packageDetailsForm.markAsPristine();
            this._responseService.showMessage("Package details added successfully");
            resolve(true);
          }
        });
      });
    } else {
      this.packageDetailsForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
      return false;
    }
  }
  

  serializeFormData(data:any):any {
    data = {
      ...data,
      packageStartDate: this._globalService.convertTripDateToString(data.packageStartDate),
      packageEndDate: this._globalService.convertTripDateToString(data.packageEndDate),
      totalDays: this.totalNoOfNights
    }
    return data;
  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext() {
    if (await this.onSubmit()) {
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return  this.packageDetailsForm.touched && (this.packageDetailsForm.dirty || this.packageDetailsForm.invalid);
  }

}
